import { forwardRef, type HTMLAttributes } from 'react'

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** `row` matches `Row`'s anatomy, `card` a lead card, `lines` a paragraph. A
   *  placeholder that is not the shape of what replaces it makes the screen
   *  jump, which is worse than a spinner. */
  variant?: 'row' | 'card' | 'lines'
  /** How many. Default 1. */
  count?: number
}

/**
 * The waiting state.
 *
 * `aria-hidden`, and the container carries `role="status"` with a word in it: a
 * screen reader gets "Loading" once, not a description of eight grey
 * rectangles. The shimmer lives behind `prefers-reduced-motion:
 * no-preference`; with motion reduced the blocks are simply there.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { variant = 'row', count = 1, className = '', ...rest },
  ref,
) {
  return (
    <div ref={ref} role="status" className={`u-skel-set ${className}`.trim()} {...rest}>
      <span className="u-sr">Loading</span>
      {Array.from({ length: Math.max(1, count) }, (_, i) => (
        <div key={i} className={`u-skel u-skel--${variant}`} aria-hidden="true">
          {variant === 'row' && (
            <>
              <span className="u-skel__lead" />
              <span className="u-skel__body">
                <span className="u-skel__line" />
                <span className="u-skel__line u-skel__line--short" />
              </span>
            </>
          )}
          {variant === 'card' && (
            <>
              <span className="u-skel__media" />
              <span className="u-skel__line" />
              <span className="u-skel__line u-skel__line--short" />
            </>
          )}
          {variant === 'lines' && (
            <>
              <span className="u-skel__line" />
              <span className="u-skel__line" />
              <span className="u-skel__line u-skel__line--short" />
            </>
          )}
        </div>
      ))}
    </div>
  )
})
